<template>
    <div :id="id" class="transition-height rounded-3xl bg-white">
        <div :style="{ top: stickyTop }" class="sticky z-20">
            <header
                :class="[{ 'rounded-b-3xl': collapsed, 'border-b': !collapsed }]"
                class="relative flex items-center justify-between gap-2 rounded-t-3xl border-gray-light bg-white p-4 before:absolute before:inset-0 before:-z-10 before:bg-[var(--c-body-bg)] before:content-['']"
            >
                <div class="flex items-center gap-3">
                    <div class="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-light">
                        <qr-code />
                    </div>
                    <h3 class="leading-lg mb-0 !text-lg !font-medium">{{ title }}</h3>
                    <span
                        class="text-gray leading-xl inline-flex min-h-[30px] items-center justify-center rounded-lg border-1 border-gray-light bg-blue-light px-3 text-xl font-medium"
                    >
                        <svg
                            v-if="count === null || count === undefined"
                            class="h-4 w-4 animate-spin text-gray-text"
                            fill="none"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25" />
                            <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" stroke-linecap="round" stroke-width="3" />
                        </svg>
                        <template v-else>{{ count }}</template>
                    </span>
                </div>
                <div
                    v-if="collapsable"
                    class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg bg-blue-light p-1"
                    @click.stop="toggleCollapsed"
                >
                    <chevron-down :class="{ '-rotate-180': !collapsed }" class="h-4 w-4 transition-all" />
                </div>
            </header>
        </div>
        <div v-if="!collapsed" class="p-4">
            <div
                v-if="fields?.length > 0"
                :class="showWorkOrderColumn ? 'grid-cols-[60px_1.25fr_1fr_2fr_1.5fr_1.25fr_1fr]' : 'grid-cols-[60px_1.25fr_1fr_2fr_1.5fr_1.25fr]'"
                class="mb-4 hidden h-10 items-center gap-4 rounded-md bg-blue-light px-4 leading-10 font-medium tracking-wide xs:grid"
            >
                <slot name="section-headers">
                    <div />
                    <div
                        v-for="field in fields"
                        :key="field.name"
                        :class="{ 'cursor-pointer': field.sortBy }"
                        class="flex items-center gap-2 truncate"
                        @click="updateSorting(field.sortBy)"
                    >
                        <div class="truncate">{{ field.name }}</div>
                        <sort-icon v-if="field.sortBy" :state="getSortableColumnState(field.sortBy)" class="relative top-1 shrink-0" />
                    </div>
                </slot>
            </div>

            <task-list-item v-if="loading" :show-skeleton="true" :show-work-order-column="showWorkOrderColumn" />

            <infinite-scroll v-else-if="propName" :data="propName" :buffer="400">
                <template #default="{ loading: loadingMore }">
                    <ol v-if="tasks?.length > 0" class="m-0 flex flex-col gap-4 p-0" role="list">
                        <li v-for="(item, idx) in sortedTasks" :key="idx">
                            <task-list-item
                                :data-id="item.id"
                                :item="item"
                                :loading="loadingTasks.includes(item.id)"
                                :selected="selectedTasks.includes(item.id)"
                                :show-work-order-column="showWorkOrderColumn"
                                @click.prevent="openTask(item)"
                                @update-selected="handleSelectionChange"
                            />
                        </li>
                    </ol>
                    <div v-else class="px-4" v-html="emptyText" />
                    <div v-if="loadingMore" class="flex justify-center pt-4">
                        <spinner />
                    </div>
                </template>
                <template #loading><span /></template>
            </infinite-scroll>

            <template v-else>
                <ol v-if="tasks?.length > 0" class="m-0 flex flex-col gap-4 p-0" role="list">
                    <li v-for="(item, idx) in sortedTasks" :key="idx">
                        <task-list-item
                            :data-id="item.id"
                            :item="item"
                            :loading="loadingTasks.includes(item.id)"
                            :selected="selectedTasks.includes(item.id)"
                            :show-work-order-column="showWorkOrderColumn"
                            @click.prevent="openTask(item)"
                            @update-selected="handleSelectionChange"
                        />
                    </li>
                </ol>
                <div v-else class="px-4" v-html="emptyText" />
            </template>
        </div>
    </div>
</template>

<script setup>
import TaskListItem from './TaskListItem.vue';
import QrCode from '../../../icons/QrCode.vue';
import { computed, ref } from 'vue';
import { InfiniteScroll } from '@inertiajs/vue3';
import ChevronDown from '../../../icons/ChevronDown.vue';
import SortIcon from '@/Pages/Assets/SortIcon.vue';
import Spinner from '@/Components/Spinner.vue';
import useStickyTop from '@/composables/useStickyTop';
import { visitModal } from '@/Utils/visitModal';

const props = defineProps({
    id: {
        type: String,
        required: true,
    },
    fields: Array,
    count: { type: [String, Number], default: null },
    tasks: Array,
    propName: {
        type: String,
        default: null,
    },
    title: String,
    collapsed: { type: Boolean, default: false },
    collapsable: { type: Boolean, default: true },
    defaultSort: { type: Object, default: () => ({ key: null, direction: 'asc' }) },
    emptyText: String,
    loading: { type: Boolean, default: false },
    showWorkOrderColumn: {
        type: Boolean,
        default: false,
    },
});

const emits = defineEmits(['update:collapsed']);

const selectedTasks = ref([]);
const loadingTasks = ref([]);
const sortBy = ref({ key: props.defaultSort?.key ?? null, direction: props.defaultSort?.direction ?? 'asc' });

const stickyTop = useStickyTop();

const sortedTasks = computed(() => {
    if (props.tasks?.length === 0) {
        return [];
    }

    const tasksCopy = [...props.tasks];

    if (sortBy.value.key === props.defaultSort?.key && sortBy.value.direction === props.defaultSort?.direction) {
        return tasksCopy;
    }

    // Status sort: server order already reflects the default direction, so the
    // only thing left to do for non-default direction is reverse.
    if (sortBy.value.key === 'status') {
        return tasksCopy.reverse();
    }

    return tasksCopy.sort((a, b) => {
        switch (sortBy.value.key) {
            case 'asset':
                return sortBy.value.direction === 'asc' ? a.asset.name.localeCompare(b.asset.name) : b.asset.name.localeCompare(a.asset.name);
            case 'location':
                const aLocationName = a.asset.location.hierarchy
                    .map((l) => l.name)
                    .reverse()
                    .join('|');
                const bLocationName = b.asset.location.hierarchy
                    .map((l) => l.name)
                    .reverse()
                    .join('|');

                return sortBy.value.direction === 'asc' ? aLocationName.localeCompare(bLocationName) : bLocationName.localeCompare(aLocationName);
            default:
                return 0;
        }
    });
});

const handleSelectionChange = (selected) => {
    if (selectedTasks.value.includes(selected.id)) {
        selectedTasks.value = selectedTasks.value.filter((id) => id !== selected.id);
    } else {
        selectedTasks.value.push(selected.id);
    }
};

const openTask = (task) => {
    if (!task.url || loadingTasks.value.includes(task.id)) {
        return;
    }

    loadingTasks.value.push(task.id);

    visitModal(task.url, { preserveScroll: true, history: true }).finally(() => {
        loadingTasks.value = loadingTasks.value.filter((id) => id !== task.id);
    });
};

const toggleCollapsed = () => {
    emits('update:collapsed', { id: props.id, collapsed: !props.collapsed });
};

const getSortableColumnState = (key) => {
    if (sortBy.value.key === key) {
        return sortBy.value.direction;
    }
    return 'none';
};

const updateSorting = (key) => {
    if (!key) {
        return;
    }
    let direction = sortBy.value.key !== key ? 'asc' : sortBy.value.direction;

    if (sortBy.value.key === key) {
        direction = sortBy.value.direction === 'asc' ? 'desc' : 'asc';
    }

    sortBy.value = { key, direction };
};
</script>
